import { useSkillFiles } from "@/api/skills";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useMemo, useState } from "react";

const OS_JUNK_RE =
  /(?:^|\/)(?:__MACOSX\/|\.DS_Store$|Thumbs\.db$|desktop\.ini$|\._)/;

export const SkillFileViewer = ({ skillId }: { skillId: string }) => {
  const { data: rawFiles = [] } = useSkillFiles(skillId);
  const files = useMemo(
    () => rawFiles.filter((f) => !OS_JUNK_RE.test(f.file_path)),
    [rawFiles],
  );
  const [selectedFile, setSelectedFile] = useState<string>("SKILL.md");
  const [fileContent, setFileContent] = useState<string>("");
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  useEffect(() => {
    if (files.length > 0 && !files.find((f) => f.file_path === selectedFile)) {
      setSelectedFile(files[0].file_path);
    }
  }, [files, selectedFile]);

  const selectedFileData = files.find((f) => f.file_path === selectedFile);

  useEffect(() => {
    if (!selectedFileData?.url) {
      setFileContent("");
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setIsLoadingContent(true);
    fetch(selectedFileData.url, { signal: controller.signal })
      .then((res) => res.text())
      .then((text) => {
        if (!cancelled) setFileContent(text);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (!cancelled) setFileContent("Failed to load file content.");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingContent(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedFileData?.url]);

  if (files.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        No files uploaded yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Select value={selectedFile} onValueChange={setSelectedFile}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select a file" />
        </SelectTrigger>
        <SelectContent>
          {files.map((file) => (
            <SelectItem key={file.file_path} value={file.file_path}>
              {file.file_path}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative border rounded-md bg-muted/30 overflow-auto max-h-[500px]">
        {isLoadingContent && (
          <div className="absolute inset-0 bg-background/50 flex items-start justify-end p-2 pointer-events-none">
            <span className="text-xs text-muted-foreground">Loading…</span>
          </div>
        )}
        <pre
          className={`p-4 text-sm font-mono whitespace-pre-wrap break-words transition-opacity duration-150 ${isLoadingContent ? "opacity-40" : "opacity-100"}`}
        >
          {fileContent || "Empty file"}
        </pre>
      </div>
    </div>
  );
};
